import { Extension, Editor } from "@tiptap/core";
import { useRef, useEffect } from "react";

import { parse, TextNode } from "node-html-parser";
import { nanoid } from "nanoid";

export type ColorOptions = {
  types: string[];
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    color: {
      /**
       * Set the text color
       */
      setColor: (color: string) => ReturnType;
      /**
       * Unset the text color
       */
      unsetColor: () => ReturnType;
    };
  }
}

export const CustomStylingTiptap = Extension.create<ColorOptions>({
  name: "palhari-custom-style",

  defaultOptions: {
    types: ["textStyle"],
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          class: {
            default: null,
            parseHTML: (element) => {
              return element.className.replace(/['"]+/g, "");
            },
            renderHTML: (attributes) => {
              if (!attributes.class) {
                return {};
              }

              // Default span class to test, nothing happens
              if (attributes.class === "cs--noop") {
                return {
                  class: "cs--noop",
                  style: "",
                };
              }

              // Inject this class
              if (attributes.class === "cs--noop--italic") {
                return {
                  class: "cs--noop--italic",
                  style: "",
                };
              }

              return {};
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setColor:
        (color) =>
        ({ chain }) => {
          return chain().setMark("textStyle", { class: color }).run();
        },
      unsetColor:
        () =>
        ({ chain }) => {
          console.log("here");

          return chain()
            .setMark("textStyle", { class: "red" })
            .removeEmptyTextStyle()
            .run();
        },
    };
  },
});

/**
 * Function that transforms <span style="..."> into <span style='...'> so as not to overlap
 * with the "(...) text" rule
 * @param html
 * @returns
 */
function replaceDoubleQuotesFromHTMLElements(html: string) {
  let inElement = false;
  let result = "";

  for (let i = 0; i < html.length; i++) {
    if (html[i] === "<") {
      inElement = true;
      result += html[i];
    } else if (html[i] === ">") {
      inElement = false;
      result += html[i];
    } else if (html[i] === '"' && inElement) {
      result += "'";
    } else {
      result += html[i];
    }
  }

  return result;
}

/**
 * Function that transforms ' into ’ only if it's outside of the element
 * @param html
 * @returns
 */
function replaceSingleQuotesFromHTMLOutsideElement(html: string) {
  let inElement = false;
  let result = "";

  for (let i = 0; i < html.length; i++) {
    if (html[i] === "<") {
      inElement = true;
      result += html[i];
    } else if (html[i] === ">") {
      inElement = false;
      result += html[i];
    } else if (html[i] === `'` && !inElement) {
      result += "’";
    } else {
      result += html[i];
    }
  }

  return result;
}

// --- NO OP FUNCTIONS ---
// Functions that allow the processing to skip no operations
let noOpMap: { [nanoid: string]: string } = {};

/**
 * Function that finds <span class="cs--noop">(...)</span> nodes
 * and removes them from the HTML tree. Saving them on a map.
 * @param html
 * @returns
 */
function excludeAndSaveNoOp(html: string) {
  const htmlNodeTree = parse(html);

  // All the nodes with the class .cs-noop that will be excluded
  const noOpNodesToExclude = htmlNodeTree.querySelectorAll(".cs--noop");

  noOpNodesToExclude.forEach((node) => {
    const id = nanoid();
    noOpMap[id] = node.outerHTML;

    const textNodeWithTheId = new TextNode(
      `_NOOP_START_@ID-${id}_NOOP_END_`,
      htmlNodeTree
    );

    // Replace the complete node with the text and id
    node.replaceWith(textNodeWithTheId);
  });

  return htmlNodeTree.outerHTML;
}

/**
 * Function that finds _NOOP_START_@ID-${id}_NOOP_END_ text
 * and inserts the saved HTML into it
 * @param html
 * @returns
 */
function restoreNoOp(html: string) {
  html = html
    .split("_NOOP_START_")
    .map((node) => {
      // Extract from text the ${id} in the format @ID-${nanoid}
      let [id, rest] = node.split("_NOOP_END_");

      if (!id.includes("@ID-")) {
        // Then it's not an id, just part of text
        return node;
      }

      // Remove the prefix
      id = id.replace("@ID-", "");

      // Map the id to it's node that was previously saved
      // Tiptap has an issue with textStyle marks without `style` properties,
      // so just add a style property for it to not complaint
      const nodeText = noOpMap[id].replace(">", ` style="">`);

      return `${noOpMap[id]}${rest}`;
    })
    .join("");

  // After all that, clear the noOpMap
  noOpMap = {};

  return html;
}

function resetHTMLStyling(html: string) {
  // Save the noop classes that must be skipped
  html = excludeAndSaveNoOp(html);

  // Remove all the italic occurences on all their possible forms
  html = html
    // Recommended way, I inject a style and render it italic through a global CSS rule. Only for viewing.
    // The EmailService on the backend is responsible to injecting the proper style
    .replaceAll(
      `<em style="font-size: inherit;"><span class="cs--noop--italic" style="">`,
      ""
    )
    .replaceAll(`<em><span class="cs--noop--italic" style="">`, "")
    .replaceAll(`<span class="cs--noop--italic" style="">`, "")
    .replaceAll(
      `<em style="font-size: inherit;"><span class="cs--noop--italic" style>`,
      ""
    )
    .replaceAll(`<em><span class="cs--noop--italic" style>`, "")
    .replaceAll(`<span class="cs--noop--italic" style>`, "")
    .replaceAll("</span></em>", "")
    .replaceAll("</span>", "");

  // Make all EM dashes into normal double dashes
  html = html.replaceAll("—", "-");

  // Replace the open and close commas
  html = html.replaceAll("“", `"`).replaceAll("”", `"`);
  html = html.replaceAll("’", `'`);

  html = replaceDoubleQuotesFromHTMLElements(html);

  return html;
}

function onEditorUpdate(editor: Editor, isExecutingComposition: boolean) {
  const html = editor.getHTML();
  const currentPosition = editor.state.selection.$anchor.pos;

  if (isExecutingComposition) return;

  // Remove all inputted text
  let sanitizedHTML = resetHTMLStyling(html);

  const sanitizedLines = sanitizedHTML.split("</p>");

  // For each line, do the replaces necessary
  const formattedLines = sanitizedLines.map((sanitizedLine) => {
    sanitizedLine = sanitizedLine
      // Detects "text"
      .replace(
        /"([^)]+?)"/g,
        `<span class='cs--noop--italic' style=''>“$1”</span>`
      )
      // Detects (text)
      .replace(
        /\(([^)]+)\)/g,
        `<span class='cs--noop--italic' style=''>($1)</span>`
      );

    sanitizedLine = sanitizedLine
      .replaceAll(" - ", " — ")
      .replaceAll(" -- ", " — ");

    sanitizedLine = replaceSingleQuotesFromHTMLOutsideElement(sanitizedLine);

    return sanitizedLine;
  });

  let formattedHTML = formattedLines.join("</p>");

  // Restore the noop classes that were skipped
  formattedHTML = restoreNoOp(formattedHTML);

  editor
    .chain()
    .setContent(formattedHTML, false, {
      preserveWhitespace: true,
    })
    .setTextSelection(currentPosition)
    .run();
}

function useIsExecutingComposition(params: {
  editor: Editor | null;
  enabled?: boolean;
}) {
  const { editor, enabled } = params;
  const isExecutingComposition = useRef<boolean>(false);

  if (!enabled) {
    // console.log("Composition is disabled");
    isExecutingComposition.current = false;
    return { isExecutingComposition };
  }
  useEffect(() => {
    if (!editor) return;

    function onCompositionStart(event: globalThis.CompositionEvent) {
      console.log("Composition start");
      isExecutingComposition.current = true;
    }

    function onCompositionUpdate(event: globalThis.CompositionEvent) {
      console.log("Composition update");
    }

    function onCompositionEnd(event: globalThis.CompositionEvent) {
      console.log("Composition end");
      isExecutingComposition.current = false;

      // if (editor)
      //   CustomStylingUtils.onEditorUpdate(
      //     editor,
      //     isExecutingComposition.current
      //   );
    }

    document.addEventListener("compositionstart", onCompositionStart);
    document.addEventListener("compositionupdate", onCompositionUpdate);
    document.addEventListener("compositionend", onCompositionEnd);

    return () => {
      document.removeEventListener("compositionstart", onCompositionStart);
      document.removeEventListener("compositionupdate", onCompositionUpdate);
      document.removeEventListener("compositionend", onCompositionEnd);
    };
  }, [editor]);

  return { isExecutingComposition };
}

/**
 * Function that identifies which custom style is active.
 *
 * If it returns "italic", then there's a class that was transformed by the editor, for eg: (test)
 * If it returns "noop", then there was a class and someone wanted to remove the italic from it,
 * making it a noop to be skipped
 * If it returns null, there's no custom styling class and tiptap can do whatever he wants with it
 */
function isCustomStyleActive(editor: Editor) {
  const nodeMarks = editor.state.selection.$anchor.marks();

  const csItalicNodeMark = nodeMarks.find(
    (nm) => nm.attrs.class === "cs--noop--italic"
  );

  if (csItalicNodeMark) return "italic" as const;

  const csNoOpNodeMark = nodeMarks.find((nm) => nm.attrs.class === "cs--noop");

  if (csNoOpNodeMark) return "noop" as const;

  return null;
}

/**
 * Function that correctly identifies and toggles between custom styles
 */
function toggleCustomStyleMark(editor: Editor) {
  const activeCustomStyle = isCustomStyleActive(editor);

  // No custom style here
  if (!activeCustomStyle) return false;

  const currentPosition = editor.state.selection.$anchor.pos;

  if (activeCustomStyle === "italic") {
    editor
      .chain()
      .extendMarkRange("textStyle")
      .setMark("textStyle", {
        class: "cs--noop",
      })
      .setTextSelection(currentPosition)
      .run();

    return true;
  } else if (activeCustomStyle === "noop") {
    editor
      .chain()
      .extendMarkRange("textStyle")
      .setMark("textStyle", {
        class: "cs--noop--italic",
      })
      .setTextSelection(currentPosition)
      .run();

    return true;
  }

  return false;
}

export const CustomStylingUtils = {
  onEditorUpdate,
  useIsExecutingComposition,

  // Utils to be used on the interface and shortcuts
  isCustomStyleActive,
  toggleCustomStyleMark,
};
